import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CSSProperties } from 'react';
import { useMe } from '@/features/auth/dados/me';
import { dados } from '@/lib/api/cliente';
import type { Corpo, Esquema } from '@/lib/api/tipos';
import { useApi } from '@/lib/servicos';

export type Tag = Esquema<'Tag'>;
export type TagResumo = Esquema<'TagResumo'>;
export type TipoDeTag = Tag['tipo'];
export type CorDeTag = Tag['cor'];
export type NovaTag = Corpo<'/api/v1/tags', 'post'>;
export type MudancaDaTag = Corpo<'/api/v1/tags/{id}', 'patch'>;

/** RN-183 do backend: até 10 tags por registro. */
export const TAGS_POR_REGISTRO = 10;

/** Paleta fixa (spec 023), na ordem do seletor; as cores vêm dos tokens `--tag-<cor>-*`. */
export const CORES_DE_TAG: readonly { cor: CorDeTag; nome: string }[] = [
  { cor: 'cinza', nome: 'Cinza' },
  { cor: 'vermelho', nome: 'Vermelho' },
  { cor: 'laranja', nome: 'Laranja' },
  { cor: 'amarelo', nome: 'Amarelo' },
  { cor: 'verde', nome: 'Verde' },
  { cor: 'petroleo', nome: 'Azul-petróleo' },
  { cor: 'azul', nome: 'Azul' },
  { cor: 'roxo', nome: 'Roxo' },
  { cor: 'rosa', nome: 'Rosa' },
  { cor: 'marrom', nome: 'Marrom' },
];

export function estiloDaTag(cor: CorDeTag): CSSProperties {
  return {
    backgroundColor: `var(--tag-${cor}-fundo)`,
    color: `var(--tag-${cor}-texto)`,
    borderColor: `var(--tag-${cor}-borda)`,
  };
}

/** Nome de cada tipo, como o painel chama o cadastro (gestão de tags). */
export const NOME_DO_TIPO: Record<TipoDeTag, string> = {
  exercicio: 'Exercícios',
  ficha: 'Fichas de treino',
  plano: 'Planos de treino',
  prescricao: 'Prescrições',
  documento: 'Documentos',
  curso: 'Cursos',
  aluno: 'Alunos',
  colaborador: 'Colaboradores',
  papel: 'Papéis',
  tipo_prescricao: 'Tipos de prescrição',
  categoria_ficha: 'Categorias de ficha',
};

/** Texto comparável: sem maiúsculas, acentos e espaços repetidos (como o `app_chave` do banco). */
export function chaveDoTexto(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** `?tags=` da URL ↔ lista de ids. */
export function idsDoFiltro(valor: string | undefined): string[] {
  return valor ? valor.split(',').filter(Boolean) : [];
}

export function filtroDosIds(ids: readonly string[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined;
}

export const tagsChaves = {
  todas: ['tags'] as const,
  doTipo: (tipo: TipoDeTag | 'todos') => ['tags', tipo] as const,
};

/** Todas as tags de um tipo (no máximo 200), por nome: o campo e o filtro buscam nelas. */
export function useTagsDoTipo(tipo: TipoDeTag, habilitado = true) {
  const api = useApi();
  return useQuery({
    queryKey: tagsChaves.doTipo(tipo),
    queryFn: ({ signal }) =>
      dados(api.GET('/api/v1/tags', { params: { query: { tipo } }, signal })).then((r) => r.data),
    enabled: habilitado,
    staleTime: 30_000,
  });
}

/** As tags de todos os tipos que o papel lê (gestão de tags). */
export function useTodasAsTags() {
  const api = useApi();
  return useQuery({
    queryKey: tagsChaves.doTipo('todos'),
    queryFn: ({ signal }) =>
      dados(api.GET('/api/v1/tags', { params: { query: {} }, signal })).then((r) => r.data),
  });
}

/** Renomear, recolorir e excluir: owner e admin (RN-186 do backend). */
export function usePodeGerirTags(): boolean {
  const { data: me } = useMe();
  return me?.papel === 'owner' || me?.papel === 'admin';
}

export function useCriarTag() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (corpo: NovaTag) => dados(api.POST('/api/v1/tags', { body: corpo })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tagsChaves.todas }),
  });
}

/** Renomear ou recolorir muda a tag em todos os registros: as listas abertas recarregam. */
export function useEditarTag() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mudanca }: { id: string; mudanca: MudancaDaTag }) =>
      dados(api.PATCH('/api/v1/tags/{id}', { params: { path: { id } }, body: mudanca })),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useExcluirTag() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      dados(api.DELETE('/api/v1/tags/{id}', { params: { path: { id } } })),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

/** Na edição que manda só o que mudou: o conjunto de tags é outro? (a ordem não importa) */
export function tagsMudaram(antes: readonly { id: string }[], depois: readonly string[]): boolean {
  const ids = new Set(antes.map((t) => t.id));
  return ids.size !== depois.length || depois.some((id) => !ids.has(id));
}
